import { useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { activeTenant } from '../../lib/tenant'
import { uploadClientPortalPaymentProofByClient } from '../leads/service'
import { openPortalPrint } from '../portal/print'
import { bindAppointmentAvailability } from '../appointments/availability'

type NoticeType = 'success' | 'error' | 'warning' | 'info'
type Row = Record<string, any>
type ClientPortalData = {
  case_code: string
  client: { id: string; code: string; name: string; email: string | null; phone: string | null }
  project: { id: string; name: string; location: string | null; status: string; progress: number | null } | null
  unit: { id: string; code: string; name: string | null; property_type: string | null; building: string | null; floor: string | null; bedrooms: number | null; bathrooms: number | null; area_m2: number | null; status: string; price: number | null; currency: string | null } | null
  plan: { id: string; plan_number: string; currency: 'USD' | 'DOP'; sale_price: number; unit_reference: string | null; status: string; start_date: string | null; due_day: number | null; number_of_installments: number | null; installment_amount: number | null; total_paid: number; balance: number } | null
  stages: Row[]
  installments: Row[]
  payments: Row[]
  submissions: Row[]
  appointments: Row[]
  progress: Row[]
}

type StoredAccess = { clientCode: string; accessToken: string }
type PaymentTarget = { id: string; kind: 'stage' | 'installment'; label: string; balance: number }

const sessionKey = `webapp_${activeTenant.code.toLowerCase()}_client_portal_session`
const escapeHtml = (value: unknown) => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' })[char]!)
const money = (value: unknown, currency = 'USD') => new Intl.NumberFormat('es-DO', { style: 'currency', currency: currency || 'USD' }).format(Number(value || 0))
const date = (value: unknown) => value ? new Date(String(value) + (String(value).length === 10 ? 'T12:00:00' : '')).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
const time = (value: unknown) => String(value || '').slice(0, 5)
const statusLabel = (value: unknown) => ({
  active: 'Activo', pending: 'Pendiente', paid: 'Pagada', partial: 'Parcial',
  overdue: 'Vencida', confirmed: 'Confirmada', completed: 'Completada',
  rescheduled: 'Reprogramada', cancelled: 'Cancelada', approved: 'Aprobado',
  rejected: 'Rechazado', reviewing: 'En revisión', submitted: 'En revisión',
  converted: 'Activo', reserved: 'Reservada', sold: 'Vendida',
}[String(value)] || String(value || '—'))
const statusClass = (value: unknown) => ['paid','completed','confirmed','approved','active'].includes(String(value))
  ? 'success' : ['rejected','cancelled','overdue'].includes(String(value))
    ? 'error' : ['reviewing','submitted','partial','rescheduled'].includes(String(value)) ? 'info' : 'warning'

function setText(id: string, value: unknown) {
  const node = document.getElementById(id)
  if (node) node.textContent = String(value ?? '—')
}

function paymentTargets(data: ClientPortalData): PaymentTarget[] {
  if (!data.plan) return []
  const installmentStageIds = new Set(data.installments.map(item => item.stage_id).filter(Boolean))
  const stages = data.stages
    .filter(item => Number(item.balance) > 0 && !['paid','cancelled','locked'].includes(String(item.status)) && !installmentStageIds.has(item.id))
    .map(item => ({ id: String(item.id), kind: 'stage' as const, label: String(item.label || 'Etapa'), balance: Number(item.balance) }))
  const installments = data.installments
    .filter(item => Number(item.balance) > 0 && !['paid','cancelled','locked'].includes(String(item.status)))
    .map(item => ({ id: String(item.id), kind: 'installment' as const, label: String(item.label || `Cuota ${item.installment_number}`), balance: Number(item.balance) }))
  return [...stages, ...installments]
}

function renderPortal(data: ClientPortalData) {
  const currency = data.plan?.currency || data.unit?.currency || 'USD'
  const next = [...data.installments]
    .filter(item => Number(item.balance) > 0 && !['paid','cancelled','locked'].includes(String(item.status)))
    .sort((a,b) => String(a.due_date || '').localeCompare(String(b.due_date || '')))[0]

  setText('clientPortalClientCode', data.client.code)
  setText('clientPortalName', String(data.client.name || 'cliente').split(' ')[0])
  setText('clientPortalUnit', data.unit?.name || data.unit?.code || data.plan?.unit_reference || 'Unidad por asignar')
  setText('clientPortalProject', data.project?.name || 'Proyecto por asignar')
  setText('clientPortalLocation', data.project?.location || 'Ubicación no registrada')
  setText('clientPortalUnitStatus', statusLabel(data.unit?.status || data.project?.status))
  setText('clientKpiPaid', data.plan ? money(data.plan.total_paid, currency) : '—')
  setText('clientKpiBalance', data.plan ? money(data.plan.balance, currency) : '—')
  setText('clientKpiNextAmount', next ? money(next.balance, currency) : '—')
  setText('clientKpiNextDate', next ? `Vence ${date(next.due_date)}` : 'Sin cuota pendiente')
  setText('clientKpiPlanStatus', statusLabel(data.plan?.status))
  setText('clientKpiPlanNumber', data.plan?.plan_number || 'Sin plan activo')
  setText('clientPlanHeadingCopy', data.plan ? `${data.plan.plan_number} · ${data.project?.name || 'Proyecto'} · ${data.plan.unit_reference || data.unit?.code || 'unidad'}` : 'Aún no tienes un plan activo.')
  setText('clientPlanSalePrice', data.plan ? money(data.plan.sale_price, currency) : '—')
  setText('clientPlanPaid', data.plan ? money(data.plan.total_paid, currency) : '—')
  setText('clientPlanBalance', data.plan ? money(data.plan.balance, currency) : '—')
  setText('clientNextActionTitle', next ? `Próximo vencimiento: ${next.label || `Cuota ${next.installment_number}`}` : 'Tu plan está al día')
  setText('clientNextActionCopy', next ? `${money(next.balance, currency)} · ${date(next.due_date)}` : 'No tienes cuotas pendientes disponibles.')

  const actionButton = document.querySelector<HTMLButtonElement>('.client-next-action__body .btn')
  if (actionButton) actionButton.hidden = !next

  const stages = document.getElementById('clientPlanStages')
  if (stages) stages.innerHTML = data.stages.map(item => `<article class="client-stage-card"><div class="client-stage-card__head"><h4>${escapeHtml(item.label)}</h4><span class="lozenge lozenge--${statusClass(item.status)}">${escapeHtml(statusLabel(item.status))}</span></div><p>${escapeHtml(item.stage_type || 'Etapa del plan')}</p><div class="progress"><span style="width:${Math.min(100, Number(item.amount) ? Number(item.amount_paid || 0) / Number(item.amount) * 100 : 0)}%"></span></div><div class="client-stage-card__amounts"><div><span>Pagado</span><strong>${money(item.amount_paid,currency)}</strong></div><div><span>Balance</span><strong>${money(item.balance,currency)}</strong></div></div></article>`).join('') || '<p class="help">No hay etapas publicadas.</p>'

  const installments = document.getElementById('clientInstallmentRows')
  if (installments) installments.innerHTML = data.installments.map(item => `<tr><td><strong>${escapeHtml(item.label || `Cuota ${item.installment_number}`)}</strong></td><td>${date(item.due_date)}</td><td>${money(item.amount_due,currency)}</td><td>${money(item.amount_paid,currency)}</td><td>${money(item.balance,currency)}</td><td><span class="lozenge lozenge--${statusClass(item.status)}">${escapeHtml(statusLabel(item.status))}</span></td></tr>`).join('') || '<tr><td colspan="6">No hay cuotas publicadas.</td></tr>'
  const installmentCards = document.getElementById('clientInstallmentCards')
  if (installmentCards) installmentCards.innerHTML = data.installments.map(item => `<article class="client-mobile-card"><div class="client-mobile-card__top"><strong>${escapeHtml(item.label || `Cuota ${item.installment_number}`)}</strong><span class="lozenge lozenge--${statusClass(item.status)}">${escapeHtml(statusLabel(item.status))}</span></div><div class="client-mobile-card__row"><span>Vencimiento</span><strong>${date(item.due_date)}</strong></div><div class="client-mobile-card__row"><span>Balance</span><strong>${money(item.balance,currency)}</strong></div></article>`).join('') || '<p class="help" style="padding:16px">No hay cuotas publicadas.</p>'

  const receiptRows = document.getElementById('clientReceiptRows')
  if (receiptRows) receiptRows.innerHTML = data.payments.map(item => `<tr><td><strong>${escapeHtml(item.receipt_number || item.invoice_number || 'Pago')}</strong></td><td>${date(item.payment_date)}</td><td>${escapeHtml(item.payment_type || 'Pago del plan')}</td><td>${escapeHtml(item.payment_method || '—')}</td><td>${money(item.amount,item.currency || currency)}</td><td><span class="lozenge lozenge--${statusClass(item.status)}">${escapeHtml(statusLabel(item.status))}</span></td><td><button class="text-link" type="button" data-client-receipt data-code="${escapeHtml(item.receipt_number || item.invoice_number || 'Pago')}" data-date="${escapeHtml(item.payment_date)}" data-concept="${escapeHtml(item.payment_type || 'Pago del plan')}" data-method="${escapeHtml(item.payment_method || '')}" data-reference="${escapeHtml(item.reference || '')}" data-amount="${Number(item.amount)}" data-currency="${escapeHtml(item.currency || currency)}">Ver recibo</button></td></tr>`).join('') || '<tr><td colspan="7">Aún no hay pagos aplicados.</td></tr>'
  const receiptCards = document.getElementById('clientReceiptCards')
  if (receiptCards) receiptCards.innerHTML = data.payments.map(item => `<article class="client-mobile-card"><div class="client-mobile-card__top"><strong>${escapeHtml(item.receipt_number || item.invoice_number || 'Pago')}</strong><span class="lozenge lozenge--${statusClass(item.status)}">${escapeHtml(statusLabel(item.status))}</span></div><div class="client-mobile-card__row"><span>Fecha</span><strong>${date(item.payment_date)}</strong></div><div class="client-mobile-card__row"><span>Monto</span><strong>${money(item.amount,item.currency || currency)}</strong></div><button class="btn" type="button" data-client-receipt data-code="${escapeHtml(item.receipt_number || item.invoice_number || 'Pago')}" data-date="${escapeHtml(item.payment_date)}" data-concept="${escapeHtml(item.payment_type || 'Pago del plan')}" data-method="${escapeHtml(item.payment_method || '')}" data-reference="${escapeHtml(item.reference || '')}" data-amount="${Number(item.amount)}" data-currency="${escapeHtml(item.currency || currency)}"><i data-lucide="receipt-text"></i> Ver recibo</button></article>`).join('') || '<p class="help" style="padding:16px">Aún no hay pagos aplicados.</p>'

  const recent = document.getElementById('clientRecentPayments')
  if (recent) recent.innerHTML = data.payments.slice(0,3).map(item => `<div class="activity"><div class="activity__icon"><i data-lucide="receipt-text"></i></div><div><strong>${escapeHtml(item.receipt_number || 'Pago aplicado')} · ${money(item.amount,item.currency || currency)}</strong><p>${date(item.payment_date)} · ${escapeHtml(item.payment_type || 'Plan de pago')}</p></div></div>`).join('') || '<p class="help">Aún no hay pagos aplicados.</p>'

  const submissions = document.getElementById('clientSubmissionList')
  if (submissions) submissions.innerHTML = data.submissions.map(item => `<div class="client-submission"><span class="client-list-icon"><i data-lucide="file-check-2"></i></span><div><strong>${escapeHtml(item.original_name || 'Comprobante')} · ${money(item.declared_amount,item.currency || currency)}</strong><p>${date(item.payment_date)} · ${escapeHtml(item.reference || 'Sin referencia')}</p></div><span class="lozenge lozenge--${statusClass(item.status)}">${escapeHtml(statusLabel(item.status))}</span></div>`).join('') || '<p class="help" style="padding:18px">No has enviado comprobantes.</p>'

  const targetSelect = document.getElementById('clientProofTarget') as HTMLSelectElement | null
  if (targetSelect) targetSelect.innerHTML = '<option value="">Seleccionar…</option>' + paymentTargets(data).map(item => `<option value="${item.kind}:${escapeHtml(item.id)}" data-amount="${item.balance}">${escapeHtml(item.label)} · ${money(item.balance,currency)}</option>`).join('')

  const appointments = document.getElementById('clientAppointmentList')
  if (appointments) appointments.innerHTML = data.appointments.map(item => `<div class="client-appointment-item"><span class="client-list-icon"><i data-lucide="calendar-check"></i></span><div><strong>${date(item.requested_date)} · ${time(item.requested_time)}</strong><p>${escapeHtml(({project_visit:'Visita al proyecto',office:'Reunión en oficina',video_call:'Videollamada'} as Record<string,string>)[item.modality] || item.modality)}</p></div><span class="lozenge lozenge--${statusClass(item.status)}">${escapeHtml(statusLabel(item.status))}</span></div>`).join('') || '<p class="help" style="padding:18px">No tienes citas solicitadas.</p>'

  const progressValue = Math.max(0, Math.min(100, Number(data.project?.progress ?? data.progress[0]?.percentage ?? 0)))
  setText('clientProjectProgressBadge', `${progressValue}%`)
  setText('clientProjectProgressValue', `${progressValue}%`)
  const progressBar = document.getElementById('clientProjectProgressBar')
  if (progressBar) progressBar.style.width = `${progressValue}%`
  const progressList = document.getElementById('clientProgressList')
  if (progressList) progressList.innerHTML = data.progress.map(item => `<div class="timeline__item"><span class="timeline__dot"><i data-lucide="hard-hat"></i></span><div><strong>${escapeHtml(item.title)}</strong><p>${date(item.report_date)} · ${escapeHtml(item.description || '')} · ${Number(item.percentage || 0)}%</p></div></div>`).join('') || '<p class="help">Aún no hay avances publicados.</p>'

  const proofDate = document.getElementById('clientProofDate') as HTMLInputElement | null
  if (proofDate && !proofDate.value) proofDate.value = new Date().toISOString().slice(0,10)
  ;(window as any).lucide?.createIcons()
}

function printClientPortal(kind: 'statement' | 'receipts', data: ClientPortalData) {
  const currency = data.plan?.currency || data.unit?.currency || 'USD'
  const clientName = data.client.name || 'Cliente'
  const property = [data.project?.name, data.unit?.name || data.unit?.code || data.plan?.unit_reference].filter(Boolean).join(' · ') || 'Por asignar'

  if (kind === 'statement') {
    const source = data.installments.length ? data.installments : data.stages
    const isInstallment = data.installments.length > 0
    return openPortalPrint({
      eyebrow: 'Cobranza · Portal del cliente',
      title: 'Estado de cuenta',
      reference: data.plan?.plan_number || data.client.code,
      meta: [
        { label: 'Cliente', value: clientName },
        { label: 'Código', value: data.client.code },
        { label: 'Proyecto / unidad', value: property },
        { label: 'Plan', value: data.plan?.plan_number || 'Sin plan activo' },
        { label: 'Precio de venta', value: data.plan ? money(data.plan.sale_price, currency) : '—' },
        { label: 'Estado', value: statusLabel(data.plan?.status) },
      ],
      columns: [
        { label: isInstallment ? 'Cuota' : 'Etapa' },
        { label: 'Vencimiento' },
        { label: 'Monto', align: 'right' },
        { label: 'Pagado', align: 'right' },
        { label: 'Balance', align: 'right' },
        { label: 'Estado', align: 'center' },
      ],
      rows: source.map(item => [
        item.label || (isInstallment ? `Cuota ${item.installment_number}` : item.stage_type || 'Etapa'),
        date(item.due_date),
        money(isInstallment ? item.amount_due : item.amount, currency),
        money(item.amount_paid, currency),
        money(item.balance, currency),
        statusLabel(item.status),
      ]),
      totals: [
        { label: 'Total pagado', value: data.plan ? money(data.plan.total_paid, currency) : '—' },
        { label: 'Saldo pendiente', value: data.plan ? money(data.plan.balance, currency) : '—' },
      ],
      notes: [
        'Este estado refleja los movimientos aplicados y validados por el equipo de Cobranza.',
        'Los comprobantes enviados permanecen pendientes hasta completar su revisión.',
      ],
    })
  }

  return openPortalPrint({
    eyebrow: 'Cobranza · Portal del cliente',
    title: 'Historial de recibos',
    reference: `REC-${data.client.code}`,
    meta: [
      { label: 'Cliente', value: clientName },
      { label: 'Código', value: data.client.code },
      { label: 'Proyecto / unidad', value: property },
      { label: 'Plan', value: data.plan?.plan_number || 'Sin plan activo' },
      { label: 'Recibos emitidos', value: data.payments.length },
      { label: 'Moneda del plan', value: currency },
    ],
    columns: [
      { label: 'Recibo' },
      { label: 'Fecha' },
      { label: 'Concepto' },
      { label: 'Método' },
      { label: 'Referencia' },
      { label: 'Monto', align: 'right' },
      { label: 'Estado', align: 'center' },
    ],
    rows: data.payments.map(item => [
      item.receipt_number || item.invoice_number || 'Pago',
      date(item.payment_date),
      item.payment_type || 'Pago del plan',
      item.payment_method || '—',
      item.reference || '—',
      money(item.amount, item.currency || currency),
      statusLabel(item.status),
    ]),
    totals: [{
      label: 'Total recibido',
      value: money(data.payments.reduce((sum, item) => sum + Number(item.amount || 0), 0), currency),
    }],
    notes: ['Solo se incluyen pagos aplicados y validados por Cobranza.'],
  })
}

function printClientReceipt(button: HTMLButtonElement, data: ClientPortalData | null) {
  return openPortalPrint({
    eyebrow: 'Cobranza · Portal del cliente',
    title: 'Recibo de pago',
    reference: button.dataset.code || 'RECIBO',
    autoPrint: false,
    meta: [
      { label: 'Cliente', value: data?.client.name || 'Cliente' },
      { label: 'Código', value: data?.client.code || '—' },
      { label: 'Proyecto / unidad', value: [data?.project?.name, data?.unit?.name || data?.unit?.code].filter(Boolean).join(' · ') || '—' },
      { label: 'Fecha de pago', value: date(button.dataset.date) },
      { label: 'Método', value: button.dataset.method || '—' },
      { label: 'Referencia', value: button.dataset.reference || '—' },
    ],
    columns: [
      { label: 'Concepto' },
      { label: 'Moneda', align: 'center' },
      { label: 'Monto recibido', align: 'right' },
    ],
    rows: [[
      button.dataset.concept || 'Pago del plan',
      button.dataset.currency || data?.plan?.currency || 'USD',
      money(button.dataset.amount, button.dataset.currency || data?.plan?.currency),
    ]],
    totals: [{
      label: 'Total recibido',
      value: money(button.dataset.amount, button.dataset.currency || data?.plan?.currency),
    }],
    notes: [
      'Este recibo corresponde a un pago aplicado y validado por Cobranza.',
      'Conserva el número de recibo para cualquier consulta.',
    ],
    signatures: [`Recibido por ${activeTenant.tradeName}`, 'Cliente'],
  })
}

export function ClientPortalBridge({ ready }: { ready: boolean }) {
  useEffect(() => {
    if (!ready || !supabase) return
    const client = supabase
    let currentData: ClientPortalData | null = null
    const appointmentForm = document.getElementById('clientAppointmentForm') as HTMLFormElement | null
    const availability = appointmentForm
      ? bindAppointmentAvailability(appointmentForm, () => currentData?.project?.id || null)
      : null
    const cleanups: Array<() => void> = []
    const open = (view: string) => (window as any).openView?.(view)
    const access = (): StoredAccess | null => {
      try { return JSON.parse(sessionStorage.getItem(sessionKey) || 'null') as StoredAccess | null }
      catch { return null }
    }
    const bind = (id: string, handler: (event: SubmitEvent) => void) => {
      const form = document.getElementById(id)
      if (!form) return
      const listener = (event: Event) => handler(event as SubmitEvent)
      form.addEventListener('submit', listener, true)
      cleanups.push(() => form.removeEventListener('submit', listener, true))
    }
    const notify = (type: NoticeType, title: string, message: string) => {
      const region = document.getElementById('clientNotificationRegion')
      if (!region) { (window as any).showToast?.(message); return }
      const icons: Record<NoticeType,string> = { success:'circle-check', error:'circle-x', warning:'triangle-alert', info:'info' }
      const item = document.createElement('div')
      item.className = `portal-notification portal-notification--${type}`
      item.setAttribute('role', type === 'error' ? 'alert' : 'status')
      item.innerHTML = `<span class="portal-notification__icon"><i data-lucide="${icons[type]}"></i></span><div class="portal-notification__copy"><strong>${escapeHtml(title)}</strong><span>${escapeHtml(message)}</span></div><button class="portal-notification__close" type="button" aria-label="Cerrar notificación"><i data-lucide="x"></i></button>`
      const dismiss = () => { item.classList.add('is-leaving'); window.setTimeout(() => item.remove(),180) }
      item.querySelector('button')?.addEventListener('click', dismiss, { once:true })
      region.prepend(item)
      while (region.children.length > 3) region.lastElementChild?.remove()
      ;(window as any).lucide?.createIcons()
      window.setTimeout(dismiss, type === 'error' ? 7000 : 5000)
    }
    const setBusy = (form: HTMLFormElement, busy: boolean, idle: string, active: string) => {
      const button = form.querySelector<HTMLButtonElement>('button[type="submit"]')
      if (!button) return
      button.disabled = busy
      button.setAttribute('aria-busy',String(busy))
      const label = button.querySelector('span')
      if (label) label.textContent = busy ? active : idle
      else button.textContent = busy ? active : idle
    }
    const load = async (credentials: StoredAccess) => {
      const { data, error } = await client.rpc('get_client_portal', {
        p_client_code: credentials.clientCode.trim().toUpperCase(),
        p_access_token: credentials.accessToken.trim().toUpperCase(),
      })
      if (error) throw error
      currentData = data as ClientPortalData
      renderPortal(currentData)
      await availability?.refresh()
      return currentData
    }
    const refresh = async () => {
      const credentials = access()
      if (!credentials) return false
      try { await load(credentials); return true }
      catch (error: unknown) {
        sessionStorage.removeItem(sessionKey)
        notify('error','La sesión terminó',error instanceof Error ? error.message : 'Vuelve a ingresar.')
        open('client-login')
        return false
      }
    }

    bind('clientLoginForm', event => {
      event.preventDefault(); event.stopImmediatePropagation()
      const form = event.currentTarget as HTMLFormElement
      if (!form.reportValidity()) return
      const fields = new FormData(form)
      const credentials = {
        clientCode: String(fields.get('client_code') || '').trim().toUpperCase(),
        accessToken: String(fields.get('access_token') || '').trim().toUpperCase(),
      }
      setBusy(form,true,'Entrar al portal','Validando…')
      void (async () => {
        try {
          const data = await load(credentials)
          sessionStorage.setItem(sessionKey,JSON.stringify(credentials))
          form.reset()
          open('client-portal')
          notify('success','Sesión iniciada',`Bienvenido, ${data.client.name}.`)
        } catch (error: unknown) {
          notify('error','No pudimos iniciar sesión',error instanceof Error ? error.message : 'Revisa tu código y clave.')
        } finally {
          setBusy(form,false,'Entrar al portal','Validando…')
        }
      })()
    })

    bind('clientProofForm', event => {
      event.preventDefault(); event.stopImmediatePropagation()
      const form = event.currentTarget as HTMLFormElement
      if (!form.reportValidity() || !currentData?.plan) return
      const credentials = access()
      if (!credentials) { open('client-login'); return }
      const fields = new FormData(form)
      const [kind,id] = String(fields.get('target') || '').split(':')
      const file = fields.get('file')
      if (!(file instanceof File) || !id) return
      setBusy(form,true,'Enviar a Cobranza','Enviando…')
      void (async () => {
        try {
          await uploadClientPortalPaymentProofByClient({
            clientCode: credentials.clientCode,
            accessToken: credentials.accessToken,
            planId: currentData!.plan!.id,
            stageId: kind === 'stage' ? id : null,
            installmentId: kind === 'installment' ? id : null,
            file,
            amount: Number(fields.get('amount')),
            currency: currentData!.plan!.currency,
            paymentDate: String(fields.get('payment_date') || ''),
            reference: String(fields.get('reference') || ''),
          })
          form.reset()
          const fileName = document.getElementById('clientProofFileName')
          if (fileName) fileName.textContent = 'JPG, PNG o PDF'
          notify('success','Comprobante enviado','Cobranza lo revisará antes de aplicarlo a tu plan.')
          await refresh()
        } catch (error: unknown) {
          notify('error','No pudimos enviar el comprobante',error instanceof Error ? error.message : 'Inténtalo nuevamente.')
        } finally {
          setBusy(form,false,'Enviar a Cobranza','Enviando…')
        }
      })()
    })

    bind('clientAppointmentForm', event => {
      event.preventDefault(); event.stopImmediatePropagation()
      const form = event.currentTarget as HTMLFormElement
      if (!form.reportValidity()) return
      const credentials = access()
      if (!credentials) { open('client-login'); return }
      const fields = new FormData(form)
      setBusy(form,true,'Solicitar cita','Enviando…')
      void (async () => {
        try {
          const { error } = await client.rpc('create_client_portal_appointment', {
            p_client_code: credentials.clientCode,
            p_access_token: credentials.accessToken,
            p_requested_date: fields.get('requested_date'),
            p_requested_time: fields.get('requested_time'),
            p_modality: fields.get('modality'),
            p_notes: fields.get('notes'),
          })
          if (error) throw error
          form.reset()
          notify('success','Cita solicitada',`${activeTenant.tradeName} confirmará el horario y actualizará su estado en el portal.`)
          await refresh()
        } catch (error: unknown) {
          notify('error','No pudimos solicitar la cita',error instanceof Error ? error.message : 'Inténtalo nuevamente.')
        } finally {
          setBusy(form,false,'Solicitar cita','Enviando…')
        }
      })()
    })

    const proofSelect = document.getElementById('clientProofTarget') as HTMLSelectElement | null
    const selectTarget = () => {
      const amount = document.getElementById('clientProofAmount') as HTMLInputElement | null
      const option = proofSelect?.selectedOptions[0]
      if (amount) {
        amount.value = option?.dataset.amount || ''
        amount.max = option?.dataset.amount || ''
      }
    }
    proofSelect?.addEventListener('change',selectTarget)
    cleanups.push(() => proofSelect?.removeEventListener('change',selectTarget))

    const proofFile = document.getElementById('clientProofFile') as HTMLInputElement | null
    const fileChange = () => {
      const label = document.getElementById('clientProofFileName')
      if (label) label.textContent = proofFile?.files?.[0]?.name || 'JPG, PNG o PDF'
    }
    proofFile?.addEventListener('change',fileChange)
    cleanups.push(() => proofFile?.removeEventListener('change',fileChange))

    const logoutButton = document.getElementById('clientLogoutButton')
    const logout = () => {
      sessionStorage.removeItem(sessionKey)
      currentData = null
      open('client-login')
      notify('info','Sesión cerrada','Saliste del portal del cliente correctamente.')
    }
    logoutButton?.addEventListener('click',logout)
    cleanups.push(() => logoutButton?.removeEventListener('click',logout))

    const viewOpened = (event: Event) => {
      if ((event as CustomEvent).detail?.name === 'client-login' && access()) {
        void refresh().then(ok => { if (ok) open('client-portal') })
      }
    }
    window.addEventListener('siga:view-opened',viewOpened)
    cleanups.push(() => window.removeEventListener('siga:view-opened',viewOpened))

    const receiptClick = (event: Event) => {
      const target = event.target as HTMLElement
      const printButton = target.closest<HTMLButtonElement>('[data-client-print]')
      if (printButton) {
        if (!currentData) {
          notify('warning','Información no disponible','Espera a que termine de cargar tu cuenta.')
          return
        }
        const opened = printClientPortal(printButton.dataset.clientPrint as 'statement' | 'receipts', currentData)
        if (!opened) notify('warning','Ventana bloqueada','Permite ventanas emergentes para abrir el documento.')
        return
      }

      const button = target.closest<HTMLButtonElement>('[data-client-receipt]')
      if (!button) return
      const opened = printClientReceipt(button, currentData)
      if (!opened) notify('warning','Ventana bloqueada','Permite ventanas emergentes para abrir el recibo.')
    }
    document.addEventListener('click',receiptClick)
    cleanups.push(() => document.removeEventListener('click',receiptClick))

    if (access()) void refresh()
    return () => { availability?.dispose(); cleanups.forEach(cleanup => cleanup()) }
  }, [ready])
  return null
}
